// Migrations operate directly on localStorage (not via storage adapter)
// to avoid triggering Firestore write-through during migration.

import { normaliseEntityColor } from './entityColor';

const MIGRATION_KEY = 'migration_version';
/* Exported so tests can assert "fully migrated" rather than a literal. A test that pins
   the number breaks every time a migration is added, which says nothing about the
   behaviour it was written to protect. */
export const CURRENT_VERSION = 5;

/**
 * Build the localStorage key for the given logical key, scoped to a user when uid is provided.
 */
function _scopedKey(key, uid) {
    const prefix = 'alphafocus_';
    return uid ? `${prefix}${uid}_${key}` : `${prefix}${key}`;
}

/**
 * Helpers that read/write localStorage directly with uid-scoped keys,
 * bypassing the storage adapter (which would trigger Firestore writes).
 */
function _localGet(key, uid, defaultValue = null) {
    try {
        const raw = localStorage.getItem(_scopedKey(key, uid));
        return raw ? JSON.parse(raw) : defaultValue;
    } catch {
        return defaultValue;
    }
}

function _localSet(key, uid, value) {
    localStorage.setItem(_scopedKey(key, uid), JSON.stringify(value));
}

/**
 * Run all pending data migrations.
 * Called after cloud pull completes (in storage.js boot sequence) with the
 * authenticated user's uid so that migrations operate on the correct
 * user-scoped localStorage keys.
 *
 * @param {string} [uid] - The authenticated user's UID. When provided,
 *   all localStorage key operations use the `alphafocus_{uid}_` prefix.
 */
export function runMigrations(uid) {
    const version = _localGet(MIGRATION_KEY, uid, 0);

    if (version < 1) {
        migrateTasksFromProjects(uid);
        _localSet(MIGRATION_KEY, uid, 1);
    }

    if (version < 2) {
        migrateFlowsToFlows(uid);
        _localSet(MIGRATION_KEY, uid, 2);
    }

    if (version < 3) {
        migrateAreaMode(uid);
        _localSet(MIGRATION_KEY, uid, 3);
    }

    if (version < 4) {
        migrateIdentityArt(uid);
        _localSet(MIGRATION_KEY, uid, 4);
    }

    if (version < 5) {
        migrateEntityColors(uid);
        _localSet(MIGRATION_KEY, uid, 5);
    }
}

/**
 * Migration v5: map recognised palette hexes onto categorical token keys.
 *
 * See docs/data-schemas.md, "Entity colour — the EntityColor contract". A stored hex
 * cannot follow the theme; a token key can. Projects, areas and identities each carry a
 * `color`, and select / multi-select property definitions carry one per option.
 *
 * ONLY EXACT, KNOWN HEXES MIGRATE. `normaliseEntityColor` returns anything else
 * unchanged — an unrecognised hex is a colour the user picked deliberately through the
 * custom input, and snapping it to the nearest palette hue would destroy that choice to
 * satisfy a tidiness goal. `resolveEntityColor` renders hexes indefinitely, so leaving
 * one costs nothing except its theme-awareness.
 */
function migrateEntityColors(uid) {
    let touched = 0;

    for (const key of ['projects', 'areas', 'identities']) {
        const rows = _localGet(key, uid, []);
        if (!Array.isArray(rows) || rows.length === 0) continue;

        const updated = rows.map(row => {
            if (!row || typeof row.color !== 'string') return row;
            const next = normaliseEntityColor(row.color);
            if (next === row.color) return row;
            touched++;
            return { ...row, color: next };
        });

        _localSet(key, uid, updated);
    }

    // Property options sit one level deeper, and only select-ish types have them.
    const properties = _localGet('properties', uid, []);
    if (Array.isArray(properties) && properties.length) {
        const updated = properties.map(prop => {
            if (!prop || !Array.isArray(prop.options)) return prop;
            let changed = false;
            const options = prop.options.map(opt => {
                if (!opt || typeof opt.color !== 'string') return opt;
                const next = normaliseEntityColor(opt.color);
                if (next === opt.color) return opt;
                changed = true;
                touched++;
                return { ...opt, color: next };
            });
            return changed ? { ...prop, options } : prop;
        });
        _localSet('properties', uid, updated);
    }

    if (touched > 0) {
        console.info(`[migration v5] mapped ${touched} entity colour(s) onto categorical tokens`);
    }
}

/**
 * Migration v4: Seed Identity.art = null and Identity.archetypeId = null on all existing identities that lack them.
 */
function migrateIdentityArt(uid) {
    const identities = _localGet('identities', uid, []);
    const updated = identities.map(i => ({
        ...i,
        art: i.art !== undefined ? i.art : null,
        archetypeId: i.archetypeId !== undefined ? i.archetypeId : null,
    }));
    _localSet('identities', uid, updated);
    console.log(`[Migration v4] Seeded art fields on ${identities.length} identities.`);
}

/**
 * Migration v3: Seed Area.mode = 'both' on all existing areas that lack it.
 */
function migrateAreaMode(uid) {
    const areas = _localGet('areas', uid, []);
    const updated = areas.map(a => a.mode ? a : { ...a, mode: 'both' });
    _localSet('areas', uid, updated);
    console.log(`[Migration v3] Seeded mode field on ${areas.length} areas.`);
}

/**
 * Migration v2: Rename 'flows' to 'flows' in local storage keys.
 */
function migrateFlowsToFlows(uid) {
    const keyMap = {
        'flows': 'flows',
        'flow_templates': 'flow_templates',
        'flow_execution': 'flow_execution',
        'flow_draft': 'flow_draft'
    };

    let migratedCount = 0;
    for (const [oldKey, newKey] of Object.entries(keyMap)) {
        const data = _localGet(oldKey, uid, null);
        if (data !== null) {
            _localSet(newKey, uid, data);
            if (oldKey !== newKey) {
                localStorage.removeItem(_scopedKey(oldKey, uid));
            }
            migratedCount++;
        }
    }
    
    if (migratedCount > 0) {
        console.log(`[Migration v2] Migrated ${migratedCount} Flow keys to Flow keys.`);
    }
}

/**
 * Migration v1: Extract nested project.tasks[] into standalone 'tasks' store.
 *
 * Before: projects = [{ ..., tasks: [{ id, title, ... }] }]
 * After:  projects = [{ ..., tasks: undefined }]
 *         tasks   = [{ id, title, projectId, ... (enriched fields) }]
 */
function migrateTasksFromProjects(uid) {
    const projects = _localGet('projects', uid, []);
    const existingTasks = _localGet('tasks', uid, []);

    // If tasks already exist in standalone store, skip
    if (existingTasks.length > 0) return;

    const migratedTasks = [];
    let sortOrder = 0;

    projects.forEach(project => {
        const projectTasks = project.tasks || [];
        projectTasks.forEach(task => {
            migratedTasks.push({
                // Carry over existing fields
                id: task.id,
                title: task.title || 'Untitled',
                description: task.description || '',
                status: task.status || 'todo',
                priority: task.priority || 'medium',
                createdAt: task.createdAt || new Date().toISOString(),

                // Link back to project
                projectId: project.id,

                // New fields with defaults
                dueDate: null,
                dueTime: null,
                startDate: null,
                scheduledDate: null,
                estimatedMinutes: null,
                actualMinutes: 0,
                subtasks: [],
                tags: [],
                notes: '',
                recurrence: null,
                identityId: null,
                areaId: null,
                blockedBy: [],
                updatedAt: task.createdAt || new Date().toISOString(),
                completedAt: task.status === 'done' ? (task.createdAt || new Date().toISOString()) : null,
                sortOrder: sortOrder++,
            });
        });
    });

    // Write migrated tasks to standalone store
    if (migratedTasks.length > 0) {
        _localSet('tasks', uid, migratedTasks);
    }

    // Remove tasks array from projects (keep projects otherwise intact)
    const cleanedProjects = projects.map(p => {
        // Destructure-to-omit: `tasks` must stay named here (not folded into `...rest`,
        // and not simply deleted) — that's what drops the embedded tasks array from the
        // object below. eslint's no-unused-vars can't see that a rest-sibling is
        // intentionally unused, so it's prefixed per this repo's own ignore convention
        // (varsIgnorePattern: '^[A-Z_]' in eslint.config.js) rather than suppressed.
        const { tasks: _tasks, ...rest } = p;
        return { ...rest, areaId: null, dueDate: null };
    });
    _localSet('projects', uid, cleanedProjects);

    console.log(`[Migration v1] Migrated ${migratedTasks.length} tasks from ${projects.length} projects.`);
}
